import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useExerciseMap } from "../hooks/useExerciseMap";
import { rescaleWeeksForUser } from "../lib/rescale";
import PublicHeader from "../components/public/PublicHeader";
import PlanWeeks from "../components/public/PlanWeeks";
import CopyCta from "../components/public/CopyCta";
import PlanReviewsSection from "../components/PlanReviewsSection";
import {
  ArrowLeft,
  Eye,
  Copy as CopyIcon,
  Target,
  User as UserIcon,
  Star,
} from "@phosphor-icons/react";

function NotFoundView() {
  return (
    <div className="min-h-screen grid place-items-center p-8">
      <div className="text-center">
        <div className="label-mono mb-2">// 404</div>
        <h1 className="font-display text-4xl font-black mb-3">PLAN NOT FOUND</h1>
        <p className="text-muted-foreground mb-6">This plan was not shared, or the link expired.</p>
        <Link to="/" className="text-volt hover:underline">Back to base</Link>
      </div>
    </div>
  );
}

function PlanMetaBadges({ plan }) {
  return (
    <div className="flex flex-wrap gap-2 mb-6">
      <span className="px-2 py-1 bg-volt text-black label-mono">{plan.type?.toUpperCase()}</span>
      {plan.goal && <span className="px-2 py-1 border border-border label-mono">GOAL: {plan.goal}</span>}
      {plan.level && <span className="px-2 py-1 border border-border label-mono">{plan.level}</span>}
      <span className="px-2 py-1 border border-border label-mono">{plan.weeks?.length || 0} WEEKS</span>
      <span className="px-2 py-1 border border-border label-mono inline-flex items-center gap-1" data-testid="public-views"><Eye size={12} weight="bold" /> {plan.view_count || 0} VIEWS</span>
      <span className="px-2 py-1 border border-volt text-volt label-mono inline-flex items-center gap-1" data-testid="public-copies"><CopyIcon size={12} weight="bold" /> {plan.copy_count || 0} OPERATORS COPIED</span>
      {plan.review_count > 0 && (
        <span className="px-2 py-1 border border-border label-mono inline-flex items-center gap-1" data-testid="public-rating-badge">
          <Star size={12} weight="fill" className="text-volt" />
          {Number(plan.avg_rating || 0).toFixed(1)} ({plan.review_count})
        </span>
      )}
    </div>
  );
}

function TuneToggle({ tuned, setTuned, count, hasMatchingLifts }) {
  if (!hasMatchingLifts) {
    return (
      <div className="border border-border bg-card p-3 mb-6 text-xs text-muted-foreground">
        // No main barbell lifts in this plan — nothing to rescale.
      </div>
    );
  }
  return (
    <div className="border border-volt/40 bg-card p-3 mb-6 flex flex-wrap items-center gap-3" data-testid="tune-toggle-bar">
      <span className="label-mono text-volt">// PREVIEW MODE</span>
      <div className="flex border border-border" data-testid="tune-toggle">
        <button
          onClick={() => setTuned(false)}
          data-testid="tune-toggle-original"
          className={`px-3 py-1.5 text-xs uppercase tracking-widest font-display ${!tuned ? "bg-volt text-black" : "text-muted-foreground hover:text-foreground"}`}
        >
          <UserIcon size={12} weight="bold" className="inline -mt-0.5 mr-1" /> Original
        </button>
        <button
          onClick={() => setTuned(true)}
          data-testid="tune-toggle-mine"
          className={`px-3 py-1.5 text-xs uppercase tracking-widest font-display ${tuned ? "bg-volt text-black" : "text-muted-foreground hover:text-foreground"}`}
        >
          <Target size={12} weight="bold" className="inline -mt-0.5 mr-1" /> Tune to me
        </button>
      </div>
      <div className="text-xs text-muted-foreground">
        {tuned
          ? <>Showing weights rescaled to <Link to="/assessment" className="text-volt hover:underline">your 1RMs</Link> ({count} {count === 1 ? "lift" : "lifts"} adjusted).</>
          : <>Showing the author's original weights. Click "Tune to me" to preview them rescaled to your 1RMs.</>}
      </div>
    </div>
  );
}

export default function PublicPlan() {
  const { slug } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const exMap = useExerciseMap();
  const [plan, setPlan] = useState(null);
  const [copying, setCopying] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [assessment, setAssessment] = useState(null); // null = unloaded; {} = none on file
  const [tuned, setTuned] = useState(false);

  useEffect(() => {
    api.get(`/public/plans/${slug}`).then((r) => setPlan(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  // Only logged-in users can preview-tune.
  useEffect(() => {
    if (!user) { setAssessment({}); return; }
    api.get("/me/assessment").then((r) => setAssessment(r.data || {})).catch(() => setAssessment({}));
  }, [user]);

  const { weeks: tunedWeeks, count: tunedCount } = useMemo(() => {
    if (!plan?.weeks) return { weeks: [], count: 0 };
    return rescaleWeeksForUser(plan.weeks, assessment || {});
  }, [plan, assessment]);

  const displayedWeeks = tuned ? tunedWeeks : plan?.weeks;
  const hasMatchingLifts = tunedCount > 0 || !!Object.keys(assessment || {}).find((k) => k.endsWith("_1rm_lb") && assessment[k]);

  const copy = async () => {
    if (!user) { navigate("/register"); return; }
    setCopying(true);
    try {
      const { data } = await api.post(`/plans/copy/${slug}`);
      const tunedMsg = data.rescaled_count > 0 ? `?tuned=${data.rescaled_count}` : "";
      navigate(`/builder/${data.id}${tunedMsg}`);
    } catch (e) {
      alert("Copy failed: " + (e.response?.data?.detail || e.message));
      setCopying(false);
    }
  };

  if (notFound) return <NotFoundView />;
  if (!plan) return <div className="min-h-screen grid place-items-center"><div className="label-mono animate-pulse">LOADING...</div></div>;

  return (
    <div className="min-h-screen flex flex-col">
      <PublicHeader user={user} copying={copying} onCopy={copy} />
      <main className="flex-1 max-w-5xl w-full mx-auto px-6 py-10">
        <Link to="/" className="inline-flex items-center gap-2 label-mono hover:text-volt mb-6"><ArrowLeft size={14} weight="bold" /> Home</Link>
        <div className="label-mono text-volt mb-2">// SHARED BY {plan.owner_name?.toUpperCase() || "OPERATOR"}</div>
        <h1 className="font-display text-5xl font-black mb-3">{plan.name}</h1>
        <PlanMetaBadges plan={plan} />
        {user && <TuneToggle tuned={tuned} setTuned={setTuned} count={tunedCount} hasMatchingLifts={hasMatchingLifts} />}
        <PlanWeeks weeks={displayedWeeks} exMap={exMap} />
        <CopyCta user={user} copying={copying} onCopy={copy} />
        <PlanReviewsSection slug={plan.share_slug} planOwnerId={plan.user_id} currentUser={user} />
      </main>
      <footer className="border-t border-border py-4 text-center label-mono">TACTICAL PROGRESSION // FM 7-22 H2F DOCTRINE</footer>
    </div>
  );
}
